import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { NewTargetsFooter } from '../../../../components/custom_components/NewTargetsFooter'
import { useTargetsModalStore } from '../../../../components/custom_components/TargetsModal/TargetsModalStore'
import { useAuth } from '../../../../context/auth-context'
import { useCurrency } from '../../../../context/currency-context'
import useManagerMeetingStepTargets from '../../../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import {
  FullMeetingFragment,
  MeetingStepStatus,
  MeetingStepViewId,
  GetManagerMeetingStepTargetsQuery,
  ObjectiveStatus,
  MeetingType,
  Currency,
  BonusBetweenAnnualBonusAndProrataDto,
} from '../../../../types'
import { ClosedTargetsComponent } from '../../okr/closedTargets/ClosedTargetsComponent'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'
import { TargetBonusOverview } from '../shared/appraisal-components/TargetBonusOverview'
import { filterObjectivesByTypeStatusAndRefYear } from '../../okr/util'
import { useBonusDistributionModalStore } from '../../../../components/custom_components/BonusDistributionModal/BonusDistributionModalStore'
import {
  useFlexibleProrataManagementEnabled,
  useFindBonusByEmployeeIdAndMeetingId,
} from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { ManagerNewObjectives } from '../../okr/ManagerNewObjectives'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { getFlexibleBonusUnknownCurrencyForConversion, toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'
import { calculateNewTargetsData } from '../shared/util'

interface AppraisalMeetingStepNewGoalsProps {
  meeting: FullMeetingFragment
}

interface AppraisalMeetingStepNewGoalsContentProps {
  data: GetManagerMeetingStepTargetsQuery
  meeting: FullMeetingFragment
}

const AppraisalMeetingStepNewGoals: FC<AppraisalMeetingStepNewGoalsProps & AppraisalStepBtns> = ({ meeting, backBtn, nextBtn }) => {
  const { data, loading, error } = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.newTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
  )

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} error={error} />
  } else {
    return <AppraisalMeetingStepNewGoalsContent data={data} meeting={meeting} backBtn={backBtn} nextBtn={nextBtn} />
  }
}

const AppraisalMeetingStepNewGoalsContent: FC<AppraisalMeetingStepNewGoalsContentProps & AppraisalStepBtns> = ({
  data,
  meeting,
  backBtn,
  nextBtn,
}) => {
  const {
    data: bonusAmountEditableData,
    error: bonusAmountEditableError,
    loading: bonusAmountEditableLoading,
  } = useFlexibleProrataManagementEnabled(data.currentContract!.legalEntity.country)

  const {
    data: findBonusAmountData,
    error: findBonusAmountDataError,
    loading: findBonusAmountLoading,
  } = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)
  const { currencyConversionData } = useCurrency()

  if (bonusAmountEditableError || findBonusAmountDataError || !bonusAmountEditableData || !findBonusAmountData) {
    return (
      <LoadingAndErrors
        isLoading={bonusAmountEditableLoading || findBonusAmountLoading}
        hasNoData={!bonusAmountEditableData}
        error={bonusAmountEditableError || findBonusAmountDataError}
        showSpinner
      />
    )
  }

  const currentYearFlexibleBonus = findBonusAmountData.findBonusByEmployeeIdInAppraisalSeasonOfMeeting
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    currentYearFlexibleBonus,
    data.currentContract!.currencyForPayments,
    currencyConversionData,
  )

  return (
    <AppraisalMeetingStepNewGoalsContentPage
      data={data}
      meeting={meeting}
      backBtn={backBtn}
      nextBtn={nextBtn}
      currentYearFlexibleBonus={currentYearFlexibleBonus}
      flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
      bonusEditable={bonusAmountEditableData.isFlexibleProrataManagementEnabled}
    />
  )
}
interface AppraisalMeetingStepNewGoalsContentPageProps {
  currentYearFlexibleBonus?: BonusBetweenAnnualBonusAndProrataDto | null
  flexibleBonusUnknownCurrency: Currency | undefined
  bonusEditable: boolean
}

const AppraisalMeetingStepNewGoalsContentPage: FC<
  AppraisalMeetingStepNewGoalsContentProps & AppraisalStepBtns & AppraisalMeetingStepNewGoalsContentPageProps
> = ({ data, meeting, nextBtn, backBtn, currentYearFlexibleBonus, flexibleBonusUnknownCurrency, bonusEditable }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { currency: fallbackCurrency, currencyConversionData } = useCurrency()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const isAppraisalUpdate = meeting.type === MeetingType.AppraisalUpdate

  const [setModalProps, cleanModalProps] = useTargetsModalStore((state) => [state.setModalProps, state.cleanModalProps], shallow)

  const referenceYear = meeting.targetsCalculationHelper.newTargetsReferenceYear
  const {
    objectivesInMeeting,
    targetBonusOfYear,
    contractChangesByEmployee,
    currentContract,
    salaryOfYear,
    branchOfficeByEmployee,
    isBonusValidationEnabled,
    contractsInYear,
  } = data

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)

  const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed

  const flexibleBonusOverride = toEligibleFlexibleBonusOverride(
    currentYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
    branchOfficeByEmployee.timezone,
    referenceYear,
    contractsInYear,
    targetBonusOfYear,
    contractChangesByEmployee,
  )

  const {
    annualBonus,
    prorataAmount,
    prorataPartials,
    flexibleBonusAmount,
    targetBonusOfTheYear,
    targetBonusRemaining,
    alreadyPlanned,
    hasBonusOrWillHaveBonus,
    cannotProceed,
    isMonetaryTargetMissing,
    tooMuchTargetBonusProvided,
    alreadyPaidObjectivesAmount,
    alreadyPlannedAndPaid,
  } = calculateNewTargetsData(
    allowedNewTargets,
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    flexibleBonusOverride,
    objectivesInMeeting,
    targetBonusOfYear,
    salaryOfYear,
    currentContract!.currencyForPayments,
    currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0,
    currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0,
    contractChangesByEmployee,
    isAppraisalUpdate,
    referenceYear,
    contractStartDate,
    currencyConversionData,
    contractEndDate,
    contractsInYear,
    flexibleBonusUnknownCurrency,
  )

  const {
    id: meetingId,
    targetsCalculationHelper: { newTargetsReferenceYear },
    employeePrepared,
  } = meeting

  let status = isAppraisalUpdate
    ? [
        ObjectiveStatus.Approved,
        ObjectiveStatus.Proposal,
        ObjectiveStatus.ProposalFromManager,
        ObjectiveStatus.MarkedAsDone,
        ObjectiveStatus.DismissedPending,
      ]
    : [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone]

  if (!employeePrepared) {
    // we filter proposal targets if employee didn't confirm preparation (reviewer can't see these targets so they aren't valid)
    status = status.filter((status) => status !== ObjectiveStatus.Proposal)
  }

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectivesInMeeting, true, status, newTargetsReferenceYear)
  const distributionButtonDisabled =
    monetaryObjectives.filter(({ status }) => [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status)).length ===
      0 ||
    monetaryObjectives.some(
      (obj) =>
        obj.currency !== currentContract!.currencyForPayments &&
        ![ObjectiveStatus.DismissedPending, ObjectiveStatus.Dismissed].includes(obj.status),
    )

  const openDistributionModal = useBonusDistributionModalStore((state) => state.openDistributionModal)

  const handleDistributionClick = () => {
    const objectives = monetaryObjectives.filter(({ status }) =>
      [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status),
    )
    openDistributionModal(meetingId, objectives, targetBonusOfTheYear, alreadyPaidObjectivesAmount, alreadyPlanned)
  }

  const existingWrongNewTargets =
    !allowedNewTargets &&
    monetaryObjectives.filter(({ status }) => [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status)).length > 0

  const tooltipText = existingWrongNewTargets
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingWrongNewTargets')
    : isMonetaryTargetMissing
    ? t('appraisalMeeting.stepNewGoals.disabledButtonLabel.missingTargets')
    : tooMuchTargetBonusProvided
    ? t('appraisalMeeting.stepNewGoals.disabledButtonLabel.textTooMuchTargetBonus')
    : ''

  const currentStepStatus = cannotProceed || existingWrongNewTargets ? MeetingStepStatus.Incomplete : MeetingStepStatus.Done
  const currencyForFooter = currentContract ? currentContract.currencyForPayments : fallbackCurrency

  useEffect(() => {
    setModalProps({
      employeeId: meeting.employee.id,
      meetingId: meeting.id,
      isAppraisalUpdate,
      hasBonusOrWillHaveBonus,
      targetBonus: targetBonusOfTheYear,
      alreadyPlanned: alreadyPlannedAndPaid,
      employeeCurrency: currentContract ? currentContract.currencyForPayments : null,
      employeeCountry: meeting.employee.currentContract ? meeting.employee.currentContract.legalEntity.country : null,
      allowedNewTargets,
      disabledMonetaryButtonTooltip: !allowedNewTargets
        ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetsNotAllowedForLowEmployee')
        : '',
      meeting,
    })
    return () => {
      cleanModalProps()
    }
  }, [
    hasBonusOrWillHaveBonus,
    isAppraisalUpdate,
    setModalProps,
    targetBonusOfTheYear,
    alreadyPlanned,
    cleanModalProps,
    currentContract,
    meeting,
    objectivesInMeeting,
    referenceYear,
    alreadyPlannedAndPaid,
    allowedNewTargets,
    t,
  ])

  return (
    <div data-e2e-appraisal-step-new-targets>
      <MeetingBackground withFooter={hasBonusOrWillHaveBonus}>
        <TargetBonusOverview
          annualBonus={annualBonus}
          bonusAmountEditable={bonusEditable}
          flexibleBonusAmount={flexibleBonusAmount}
          currentContract={currentContract!}
          targetBonusList={targetBonusOfYear}
          contractsInYear={contractsInYear}
          openContractChanges={contractChangesByEmployee}
          meeting={meeting}
          prorataAmount={prorataAmount}
          prorataPartials={prorataPartials}
          employeeTimezone={branchOfficeByEmployee.timezone}
          paidOutBonus={alreadyPaidObjectivesAmount}
          plannedBonus={alreadyPlanned}
          remainingBonus={targetBonusRemaining}
          distributionAvailable={!!monetaryObjectives.length}
          isAppraisalUpdate={!!isAppraisalUpdate}
          onDistributionClick={handleDistributionClick}
          distributionButtonDisabled={distributionButtonDisabled}
        />

        <ManagerNewObjectives
          meeting={meeting}
          objectives={objectivesInMeeting}
          hasBonusOrWillHaveBonus={hasBonusOrWillHaveBonus}
          isAppraisalUpdate={isAppraisalUpdate}
          existingWrongNewTargets={existingWrongNewTargets}
        />

        {isAppraisalUpdate && <ClosedTargetsComponent objectives={objectivesInMeeting} />}
      </MeetingBackground>

      {hasBonusOrWillHaveBonus && (
        <div data-e2e-appraisal-new-targets-footer>
          <AppraisalFooter>
            <NewTargetsFooter
              targetBonus={targetBonusOfTheYear}
              plannedBonus={alreadyPlanned}
              remainingBonus={targetBonusRemaining}
              cannotProceed={cannotProceed}
              formatCurrencyCallback={(amount) => formatCurrency(amount, currencyForFooter)}
            />
          </AppraisalFooter>
        </div>
      )}

      <PerFlexRow justify="space-between">
        {backBtn && backBtn(currentStepStatus)}
        {nextBtn && nextBtn(currentStepStatus, false, tooltipText)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.NewTargets} stepStatus={currentStepStatus} />
    </div>
  )
}

export default AppraisalMeetingStepNewGoals
